// @flow
import React from 'react';
import styled from 'styled-components';
import { INK_TOOLING_MEDIA_URL } from './utils/constants';
import { sidebarIcons } from './utils/sidebarData';
import { Link, useLocation } from 'react-router-dom';

const SidebarContainer = styled.div`
  position: sticky;
  top: 8px;
  width: ${props => (props.collapsed ? '60px' : '80px')};
  min-width: ${props => (props.collapsed ? '60px' : '80px')};
  height: calc(100vh - 10px);
  background-color: ${({ theme }) => theme.colors.white};
  border-right: 1px solid ${({ theme }) => theme.colors.borderLight};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4px;
  transition: width 0.3s ease;
  overflow-y: auto;
  overflow-x: hidden;
`;

const SidebarToggleWrapper = styled.div`
  display: flex;
  height: 16px;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  padding: 4px;
  padding-bottom: 8px;
`;

const SidebarToggle = styled.button`
  width: 16px;
  height: 16px;
  border: none;
  background-color: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.grayNeutralLight};
  flex-shrink: 0;
  outline: none;
  border: none;
`;

const SidebarDivider = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.colors.grayNeutralLight};
  align-self: stretch;
  margin-bottom: 8px;
`;

const SidebarIconsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  a:hover {
    text-decoration: none;
    color: ${({ theme }) => theme.colors.deepIndigo};
    font-weight: 700;
  }
`;

const SidebarIcon = styled.div`
  position: relative;
  width: ${props => (props.collapsed ? '40px' : '68px')};
  height: ${props => (props.collapsed ? '40px' : '68px')};
  border-radius: 8px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  background-color: ${({ theme, active }) =>
    active ? theme.colors.backgroundMuted : theme.colors.sidebarIconBg};

  color: ${({ theme, active }) =>
    active ? theme.colors.mainPurple : theme.colors.textPrimary};

  /* BORDER = primary signal */
  border: 1px solid
    ${({ theme, active }) =>
      active ? theme.colors.deepIndigo : 'transparent'};

  /* MICRO LIFT */
  transform: ${({ active }) => (active ? 'translateY(-1px)' : 'none')};

  /* SOFT, TIGHT SHADOW */
  box-shadow: ${({ active }) =>
    active
      ? '0 2px 6px rgba(0, 0, 0, 0.12)'
      : 'none'};

  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease,
    border-color 0.15s ease,
    background-color 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.lightSilver};
  }

  .sidebar-icon-name {
    font-size: 10px;
    font-weight: ${({ active }) => (active ? 600 : 500)};
    line-height: 10px;
    text-align: center;
    color: inherit;
    width: 60px;
    text-align: center;
  }
`;

type Props = {
  collapsed: boolean;
  activeIcon: string;
  onToggle: () => void;
  onIconClick: (iconId: string) => void;
};

const HomePageSidebar: React.FC<Props> = ({
  collapsed,
  activeIcon,
  onToggle,
  onIconClick
}) => {
  const { pathname } = useLocation();

  return (
    <SidebarContainer collapsed={collapsed} onClick={onToggle}>
      <SidebarToggleWrapper>
        <SidebarToggle onClick={onToggle}>
          <img
            width="12" height="12"
            src={`${INK_TOOLING_MEDIA_URL}/${collapsed ? 'arrow-right.svg' : 'arrow-left.svg'}`}
            alt={collapsed ? 'Open Sidebar' : 'Close Sidebar'}
          />
        </SidebarToggle>
      </SidebarToggleWrapper>
      <SidebarDivider />
      <SidebarIconsWrapper onClick={(e) => e.stopPropagation()}>
        {sidebarIcons.map((item) => {
          const isActive =
            pathname === item.route ||
            pathname.startsWith(`${item.route}/`);

          return (
            <SidebarIcon
              key={item.id}
              as={Link}
              to={item.route}
              collapsed={collapsed}
              active={isActive}
            >
              {item.icon}
              {!collapsed && (
                <span className="sidebar-icon-name">{item.name}</span>
              )}
            </SidebarIcon>
          );
        })}
      </SidebarIconsWrapper>
    </SidebarContainer>
  );
};

export default React.memo(HomePageSidebar);
